/*
 * This component is responsible for state transitions.
 *
 * @author Cargomatic Engineering
 * @usage <StateTransition />
 *
 */
import PropTypes from 'prop-types';

import React from 'react';
import classnames from 'classnames';
import { OverlayTrigger, Glyphicon, Tooltip } from 'react-bootstrap';
import { humanize, titleize } from 'underscore.string';
import { sluggify } from 'utils/localization/utils';
import { Translation } from 'react-i18next';

const getStatefulButton = (state, direction) => {
  return state.isValid
    ? getButton(state, direction)
    : getOverlayButton(state, direction);
};

const getOverlayButton = (state, direction) => {
  return (
    <OverlayTrigger
      placement="top"
      overlay={
        <Tooltip id={`transition_${direction}`}>{state.reason || titleize(humanize(state.label))}</Tooltip>
      }
    >
      {getButton(state, direction)}
    </OverlayTrigger>
  );
};

const getButton = (state, direction) => {
  /*
   * Hover events do not fire on disabled elements so this work around is necessary
   * Reference: https://github.com/react-bootstrap/react-bootstrap/issues/2428
   */
  const wrapperClass = classnames({
    'cursor-not-allowed': !state.isValid,
    'px-2': true,
  });

  const buttonClass = classnames({
    button: true,
    'button-hollow-red': direction === 'left',
    'button-hollow-green': direction === 'right',
    'pointer-events-none': !state.isValid, // Required on disabled elements for the tooltip
    'cursor-pointer': state.isValid,
  });

  return (
    (<div data-testid="div-78ec248f" className={wrapperClass}>
      <button
        data-testid="button-4cf2fd56"
        className={buttonClass}
        disabled={!state.isValid}
        onClick={() => state.onClick(state.label)}>
        <div data-testid="div-dbccfcfc" className="flex items-center space-x-1">
          {direction === 'left' && <Glyphicon glyph="chevron-left" />}
          <span data-testid="span-b6b993e3"><Translation>{(t) => (t(sluggify(state.label || '')))}</Translation></span>
          {direction === 'right' && <Glyphicon glyph="chevron-right" />}
        </div>
      </button>
    </div>)
  );
};

const StateTransition = (props) => {
  const { regress, progress, onClick, dataTestId } = props;

  /* This is dirty... */
  if (regress) regress.onClick = onClick;
  if (progress) progress.onClick = onClick;

  return (
    (<div
      data-testid={dataTestId}
      className="flex items-center justify-start -mx-2">
      {regress && getStatefulButton(regress, 'left')}
      {progress && getStatefulButton(progress, 'right')}
    </div>)
  );
};

StateTransition.propTypes = {
  regress: PropTypes.object, // The transition backward
  progress: PropTypes.object, // The transition forward
};

StateTransition.defaultProps = {
  regress: {
    label: 'Back',
    isValid: true,
    reason: '',
  },
  progress: {
    label: 'Forward',
    isValid: false,
    reason: 'Cause stone cold said so!',
  },
  onClick: () => {
    console.log('Implement Me!');
  },
};

export default StateTransition;
